// UPPDRAG-24 — searchable customer/player selector for the AdminTeams
// flow. Used by CreateTeamDialog (replaces the unmanageable flat
// dropdowns from UPPDRAG-23 once an org grows past ~50 customers).
//
// UPPDRAG-60: now also surfaces unregistered customers (rows with
// `user_id IS NULL`) so admins can add them to team_members via
// `customer_id` instead of `user_id`. Captain selection still requires
// a registered user — pass `requireRegistered={true}` from that call
// site so the original behaviour is preserved.
//
// Design choices:
//   - Min 2 chars before issuing a query (don't load 5000 rows on every
//     keystroke; same threshold as Step2Private.tsx)
//   - 300ms debounce on the input (same pattern as the booking-wizard
//     player search)
//   - Org-scoped via customers.organization_id (RLS also enforces)
//   - ilike on full_name OR email — typeable in either form
//   - Hides already-selected user_ids AND already-selected customer_ids
//     (passed via excludeUserIds + excludeCustomerIds)
//   - Clear-on-select so the parent can use multi-add UX without juggling
//     ref'd input state
//
// Note: searches `customers` not `profiles` because:
//   1. Admin context — we want org-scoped customers, not all platform users
//   2. We need user_id (nullable post-UPPDRAG-60) AND email (for fuzzy match);
//      customers carries both in one row
//   3. is_archived = false filter excludes deactivated customers

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export interface SearchablePlayer {
  customer_id: string;
  /** UPPDRAG-60: nullable when the customer is unregistered (no auth.users row yet). */
  user_id: string | null;
  full_name: string;
  email: string | null;
  /** UPPDRAG-60: derived convenience flag (`user_id !== null`). */
  isRegistered: boolean;
}

interface SearchablePlayerSelectorProps {
  organizationId: string;
  onSelect: (player: SearchablePlayer) => void;
  /** user_ids to hide from results (e.g. already-selected player1 + primaries). */
  excludeUserIds?: string[];
  /** UPPDRAG-60: customer_ids to hide from results — needed when an unregistered
   *  customer is already selected (no user_id to exclude on). */
  excludeCustomerIds?: string[];
  /** UPPDRAG-60: when true, only registered customers (with user_id) are shown.
   *  Default false → both registered and unregistered are searchable.
   *  TASK-66 PR-D: AdminTeams.tsx no longer sets this true — the player1
   *  slot now accepts unregistered customers (PR-A's CHECK constraints
   *  handle the dual-identity invariant). */
  requireRegistered?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** Optional empty-state extra UI (e.g. a "Bjud in via email"-CTA when 0 results). */
  emptyStateExtra?: React.ReactNode;
}

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const RESULT_LIMIT = 15;

export default function SearchablePlayerSelector({
  organizationId,
  onSelect,
  excludeUserIds = [],
  excludeCustomerIds = [],
  requireRegistered = false,
  placeholder,
  autoFocus,
  emptyStateExtra,
}: SearchablePlayerSelectorProps) {
  const { t } = useTranslation("admin");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchablePlayer[]>([]);
  const [searching, setSearching] = useState(false);

  // Debounced search effect. exclude keys are stable join-keys so changes
  // to the parent's selection list re-trigger the query (which then filters
  // out the new exclusions).
  const excludeUserKey = excludeUserIds.slice().sort().join(",");
  const excludeCustomerKey = excludeCustomerIds.slice().sort().join(",");
  useEffect(() => {
    if (query.length < MIN_CHARS) {
      setResults([]);
      setSearching(false);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        let q = supabase
          .from("customers")
          .select("id, user_id, full_name, email")
          .eq("organization_id", organizationId)
          .eq("is_archived", false)
          .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
          .order("full_name", { ascending: true })
          .limit(RESULT_LIMIT);
        // UPPDRAG-60: gate on user_id only when the caller requires a
        // registered customer. Default flow shows BOTH registered AND
        // unregistered customers so admins can add unregistered teammates
        // as primaries/reserves.
        if (requireRegistered) {
          q = q.not("user_id", "is", null);
        }
        const { data, error } = await q;
        if (error) throw error;
        // Narrow row shape: full_name is required for display (matchi-imported
        // profiles can have null full_name; we skip those — no actionable label).
        const filtered: SearchablePlayer[] = [];
        for (const row of data ?? []) {
          if (!row.full_name) continue;
          if (row.user_id && excludeUserIds.includes(row.user_id)) continue;
          if (excludeCustomerIds.includes(row.id)) continue;
          filtered.push({
            customer_id: row.id,
            user_id: row.user_id ?? null,
            full_name: row.full_name,
            email: row.email ?? null,
            isRegistered: row.user_id !== null,
          });
        }
        setResults(filtered);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [query, organizationId, excludeUserKey, excludeCustomerKey, requireRegistered]);

  const hasQuery = query.length > 0;
  const tooShort = hasQuery && query.length < MIN_CHARS;
  const noResults =
    !tooShort && !searching && query.length >= MIN_CHARS && results.length === 0;

  return (
    <div>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder ?? t("teams.searchPlayer", "Sök spelare (namn eller e-post)")}
        autoFocus={autoFocus}
      />
      {tooShort && (
        <p className="text-xs text-muted-foreground mt-1">
          {t("teams.searchTooShort", "Skriv minst 2 tecken")}
        </p>
      )}
      {searching && (
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("teams.searching", "Söker…")}
        </p>
      )}
      {noResults && (
        <div className="mt-1 space-y-2">
          <p className="text-xs text-muted-foreground">
            {t("teams.noPlayersFound", "Inga matchande spelare")}
          </p>
          {emptyStateExtra}
        </div>
      )}
      {results.length > 0 && (
        <ul className="mt-1 border rounded-md max-h-48 overflow-auto bg-card">
          {results.map((r) => (
            // UPPDRAG-60: key on customer_id (stable for both registered and
            // unregistered rows; user_id is nullable post-migration).
            <li key={r.customer_id}>
              <button
                type="button"
                onClick={() => {
                  onSelect(r);
                  setQuery("");
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between gap-2"
              >
                <span className="font-medium truncate flex items-center gap-2">
                  {r.full_name}
                  {/* UPPDRAG-60: badge unregistered customers so admins can
                      see at a glance which selections will create a
                      team_members row via customer_id. */}
                  {!r.isRegistered && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal">
                      {t("teams.notRegistered", "Ej registrerad")}
                    </Badge>
                  )}
                </span>
                {r.email && (
                  <span className="text-muted-foreground text-xs truncate">
                    {r.email}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
